/**
 * Where a client is signed in — its account holder and every team member — on
 * the client page's Portal access tab (``client_auth.sessions``, docs/40).
 *
 * Signing the client out everywhere is its own permission
 * (``crm.portal_access.sign_out``): it interrupts the client's work, which
 * changing its login does not. Absent without it. Each device stops at its next
 * request and lands on its sign-in, told it was signed out — never by whom.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  QueryPending,
  api,
  formatDateTime,
  formatRelative,
  toast,
  useCan,
} from '@connect/web-shared';

export interface ClientSession {
  public_id: string;
  via: 'app' | 'browser';
  device: string;
  signed_in_at: string;
  last_active_at: string;
  ip_address: string | null;
  expires_at: string;
  /** The account holder's name, or the team member's. */
  who: string;
  team_member: boolean;
}

export function ClientSessionsCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canSignOut = useCan('crm.portal_access.sign_out');
  const endpoint = `/crm/clients/${clientPublicId}/portal-access/sessions`;
  const key = ['crm', 'clients', clientPublicId, 'portal-sessions'];
  const sessions = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<{ items: ClientSession[] }>(endpoint)).items,
  });
  const [confirming, setConfirming] = useState(false);

  const signOutAll = useMutation({
    mutationFn: () => api.post<{ sessions_ended: number }>(`${endpoint}/sign-out-all`),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success(
        'Signed out everywhere',
        `${clientName}: ${result.sessions_ended} device${result.sessions_ended === 1 ? '' : 's'}.`,
      );
    },
    onError: (error) =>
      toast.error('Could not sign the client out', error instanceof ApiError ? error.message : undefined),
  });

  if (sessions.data === undefined) return <QueryPending query={sessions} />;
  const items = sessions.data;

  return (
    <Card>
      <CardHeader
        title="Signed-in devices"
        description={
          items.length
            ? `Signed in on ${items.length} device${items.length === 1 ? '' : 's'}, the team's included. The client can sign out its own under Signed-in devices in its app.`
            : 'Not signed in anywhere.'
        }
        actions={
          canSignOut && items.length > 0 ? (
            <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
              <LogOut className="size-3.5" />
              Sign out everywhere
            </Button>
          ) : null
        }
      />
      {items.length > 0 ? (
        <CardBody className="p-0">
          <ul className="divide-y divide-line">
            {items.map((session) => (
              <li key={session.public_id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm text-body">
                    {session.device}
                    {session.via === 'app' ? <Badge tone="neutral">Connect app</Badge> : null}
                    {session.team_member ? <Badge tone="accent">{session.who}</Badge> : null}
                  </p>
                  <p className="truncate text-xs text-muted">
                    <span title={formatDateTime(session.last_active_at)}>
                      Last active {formatRelative(session.last_active_at)}
                    </span>
                    {session.ip_address ? ` · ${session.ip_address}` : ''}
                    {` · signed in ${formatDateTime(session.signed_in_at)}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </CardBody>
      ) : null}

      <Confirm
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Sign ${clientName} out everywhere?`}
        message="Every device — the account holder's and each team member's — is signed out at once and has to sign in again. Their password does not change."
        confirmLabel="Sign out everywhere"
        onConfirm={async () => {
          await signOutAll.mutateAsync();
        }}
      />
    </Card>
  );
}
